import { EditInvestorPageHouseholdSectionQuery, Role } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import Input from '@/components/basicComponents/input';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import RadioButton from '@/components/basicComponents/radioButton';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import Label from '@/components/basicComponents/label';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { useErrorMessages } from '@/hooks/i18n';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { ApolloQueryResult, useMutation, useQuery } from '@apollo/client';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { editInvestorHouseholdClientAdvisorOptions } from '../constants';
import { EDIT_INVESTOR_PAGE_LIST_USERS_QUERY, UPDATE_INVESTOR_MUTATION } from '../queries';
import {
  EditInvestorFormDevider,
  EditInvestorFormFieldsWrapper,
  EditInvestorFormFooter,
  EditInvestorModalPageForm,
  EditInvestorModalPageWrapper
} from '../styles';
import { EditInvestorHouseholdClientAdvisorOptionEnum, IEditInvestorFormData } from '../types';

interface EditHouseholdModalPageProps {
  householdFormData: IEditInvestorFormData['household'];
  householdSectionDataRefetch: () => Promise<ApolloQueryResult<EditInvestorPageHouseholdSectionQuery>>;
  handleCloseModalPage: () => void;
}

export const EditHouseholdModalPage = ({ householdFormData, householdSectionDataRefetch, handleCloseModalPage }: EditHouseholdModalPageProps) => {
  const params = useParams();
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();
  const {
    control,
    formState: { isSubmitting },
    reset,
    watch,
    handleSubmit
  } = useForm<IEditInvestorFormData['household']>();

  const investorId = params?.investorId || '';

  const [advisorsList, setAdvisorsList] = useState<ISelectValue[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [updateInvestor] = useMutation(UPDATE_INVESTOR_MUTATION);

  const { loading: listUsersLoading } = useQuery(EDIT_INVESTOR_PAGE_LIST_USERS_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !householdFormData?.tenantId,
    variables: {
      filters: {
        roles: [Role.AdvisorLead, Role.AdvisorMember, Role.AdvisorAssociate],
        tenantId: [householdFormData?.tenantId]
      }
    },
    onCompleted: (data) => {
      setAdvisorsList(data?.listUsers?.map((user) => ({ value: user.id, label: `${user.firstName} ${user.lastName}` })) ?? []);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  useEffect(() => {
    if (!householdFormData || listUsersLoading) return;
    reset({
      ...householdFormData,
      clientAdvisorOption:
        householdFormData.advisorId && advisorsList?.length
          ? EditInvestorHouseholdClientAdvisorOptionEnum.AssignClientAdvisor
          : EditInvestorHouseholdClientAdvisorOptionEnum.None,
      advisorId: householdFormData.advisorId && advisorsList?.length ? householdFormData.advisorId : ''
    });
  }, [householdFormData, listUsersLoading, advisorsList]);

  const onSubmit = async (data: IEditInvestorFormData['household']) => {
    try {
      setErrorMessage(null);

      const { name, advisorId, clientAdvisorOption } = data;

      await updateInvestor({
        variables: {
          data: {
            id: investorId,
            name,
            advisorId: clientAdvisorOption === EditInvestorHouseholdClientAdvisorOptionEnum.None ? 'None' : advisorId
          }
        }
      });

      await householdSectionDataRefetch();
      handleCloseModalPage();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    }
  };

  if (listUsersLoading) {
    return <LoaderOnWholeScreen />;
  }

  return (
    <>
      {isSubmitting && (
        <ModalWrappedContent isOpen={isSubmitting} title="Saving" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleCloseModalPage} backToTitle="Edit Investor" />} />
        <PageTitle title="Household" />
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <EditInvestorModalPageWrapper>
          <EditInvestorModalPageForm onSubmit={handleSubmit(onSubmit)}>
            <EditInvestorFormFieldsWrapper>
              <Controller
                name="name"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'Household Name is required'
                  }
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    value={value || ''}
                    onChange={onChange}
                    errorText={error?.message}
                    label="Household Name"
                    styleType="form"
                    size="md"
                    isRequiredField
                    tooltipWidth={400}
                    tooltip="Investors can consist of one or more entities.  The household represents the client relationship overall. Give this household a user-friendly name."
                  />
                )}
              />
              {!!advisorsList.length && (
                <ClientAdvisorWrapper>
                  <Label size="md">Client Advisor</Label>
                  <ClientAdvisor isMobile={isMobile}>
                    <Controller
                      name="clientAdvisorOption"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <OptionsWrapper>
                          {editInvestorHouseholdClientAdvisorOptions.map((option, index) => (
                            <RadioButton
                              key={option + index}
                              name="clientAdvisor"
                              radioButtonText={option}
                              selectedOption={value}
                              handleRadioChange={(event) => onChange(event.target.value)}
                            />
                          ))}
                        </OptionsWrapper>
                      )}
                    />
                    <AdvisorSelectWrapper>
                      <AnimatePresence>
                        {watch('clientAdvisorOption') === EditInvestorHouseholdClientAdvisorOptionEnum.AssignClientAdvisor && (
                          <motion.div key="advisorSelect" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                            <Controller
                              name="advisorId"
                              control={control}
                              rules={{
                                required: {
                                  value: true,
                                  message: 'Advisor is required'
                                }
                              }}
                              render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <Select
                                  value={value || ''}
                                  setValue={onChange}
                                  options={advisorsList}
                                  errorText={error?.message}
                                  defaultSelectLabel="Assign a client advisor"
                                  label="Advisor"
                                  styleType="form"
                                  size="md"
                                  width={isMobile ? '100%' : '500px'}
                                  isRequiredField
                                />
                              )}
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </AdvisorSelectWrapper>
                  </ClientAdvisor>
                </ClientAdvisorWrapper>
              )}
              <EditInvestorFormDevider />
            </EditInvestorFormFieldsWrapper>
            <EditInvestorFormFooter>
              <Button styleType="outline" onClick={handleCloseModalPage} type="button" size="lg">
                Cancel
              </Button>
              <Button styleType={isSubmitting ? 'disabled' : 'default'} type="submit" size="lg">
                Save
              </Button>
            </EditInvestorFormFooter>
          </EditInvestorModalPageForm>
        </EditInvestorModalPageWrapper>
      </MainWrap>
    </>
  );
};

const ClientAdvisorWrapper = styled.div`
  color: ${themeVar('font.base')};
`;

const OptionsWrapper = styled.div`
  max-width: 300px;
  width: 100%;
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
`;

const ClientAdvisor = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: flex-start;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: ${({ isMobile }) => (isMobile ? '10px' : '0')};
`;

const AdvisorSelectWrapper = styled.div`
  width: 100%;
`;
